import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, User2, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

export default function AccountPage() {
    const { user } = useAuth();
    const [cases, setCases] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/cases");
                setCases(data || []);
            } catch {
                /* ignore */
            }
        })();
    }, []);

    return (
        <div className="min-h-[80vh] bg-brand-50/20 py-10">
            <div className="container max-w-4xl">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                            <User2 className="h-4 w-4" /> Mein Konto
                        </div>
                        <h1 className="mt-1 font-display text-3xl font-bold sm:text-4xl">
                            Hallo, {user?.name || user?.email}
                        </h1>
                        <p className="mt-1 text-sm text-ink-500">Ihr vertraulicher Bereich – Ergebnisse, Empfehlungen und nächste Schritte.</p>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/check">
                            <Button variant="outline" className="rounded-full" data-testid="account-new-check">Neuer Check</Button>
                        </Link>
                        <Link to="/ergebnis">
                            <Button className="rounded-full bg-brand-500 hover:bg-brand-600" data-testid="account-result">Aktuelles Ergebnis</Button>
                        </Link>
                    </div>
                </div>

                {/* Ergebnisse */}
                <div className="mt-8 rounded-3xl border border-ink-300/30 bg-white p-6">
                    <h2 className="font-display text-xl font-bold">Ihre Fälle</h2>
                    {cases.length === 0 ? (
                        <div className="mt-4 rounded-2xl bg-brand-50/60 p-5 text-sm text-ink-700">
                            Noch kein Fall gespeichert. Starten Sie den kostenlosen Check.
                            <Link to="/check" className="ml-2 font-semibold text-brand-700">
                                Check starten <ArrowRight className="inline h-3 w-3" />
                            </Link>
                        </div>
                    ) : (
                        <ul className="mt-4 divide-y divide-ink-300/30">
                            {cases.map((c) => (
                                <li key={c.case_id} className="flex items-center justify-between gap-4 py-4">
                                    <div>
                                        <div className="text-sm font-medium text-ink-900">
                                            {new Date(c.created_at).toLocaleString("de-DE")}
                                        </div>
                                        <div className="text-xs text-ink-500">
                                            Risiko: {c.risk_level} · Paket: {c.package_recommendation} · Region: {c.answers?.region || "—"}
                                        </div>
                                    </div>
                                    <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
                                        {c.package_recommendation}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Documents section */}
                <div className="mt-6 rounded-3xl border border-brand-200 bg-brand-50/60 p-6">
                    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                        <div className="flex items-start gap-3">
                            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white text-brand-600">
                                <FileText className="h-5 w-5" />
                            </span>
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-brand-700">Neu in Phase 2</div>
                                <div className="font-display text-lg font-semibold">Sichere Dokumentenprüfung</div>
                                <p className="mt-1 text-sm text-ink-500">
                                    Heimverträge, Bescheide, Kostenblätter und Rentennachweise hochladen und
                                    automatisch von KI vorprüfen lassen.
                                </p>
                            </div>
                        </div>
                        <Link to="/meine-dokumente">
                            <Button className="rounded-full bg-brand-500 hover:bg-brand-600" data-testid="account-documents-link">
                                Dokumente verwalten <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </div>

                <div className="mt-6 rounded-3xl border border-ink-300/30 bg-white p-6">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                        <Sparkles className="h-4 w-4" /> Nächster sinnvoller Schritt
                    </div>
                    <div className="mt-2 text-ink-700">
                        Lassen Sie Ihren Fall strukturiert einordnen – wir empfehlen individuell das passende
                        Paket und nehmen diskret Kontakt zu Ihnen auf.
                    </div>
                    <Link to="/kontakt" className="mt-4 inline-block">
                        <Button className="rounded-full bg-brand-500 hover:bg-brand-600" data-testid="account-request">
                            Persönliche Analyse anfragen
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
